/**
 * BuildReadyStrip — the project's launch countdown under its title (Matt, 2026-10-25: "very prominently
 * displayed internally as a target to get the project build ready. Like NASA trying to get a rocket ready
 * for launch. With a buffer score, too."). Internal only: never on a client surface. docs/ALDERFLOW.md §5c2.
 *
 * Reads api/alderflow.js `buildReady` once per session (all projects, about a second) and re-reads on the
 * `build-ready-changed` event a target change fires. Working days to the target, the buffer bar (burned /
 * left), and where the work stands against it. Before a project has a target it says so, with the proposal.
 */
import { useEffect, useState } from 'react'
import { getBuildReady, setBaselineStart } from '../services/alderflow.js'
import { hasModule } from '../services/tenancy.js'
import { promptDialog, alertDialog } from './Dialog.jsx'
import { ctx } from '../outline/ctx.js'

let cache = null   // Promise<{ today, projects }>
function load(force) { if (!cache || force) cache = getBuildReady().catch(() => { cache = null; return null }); return cache }
/** The same cached read, for the Gantt's Build Ready column and buffer row. */
export const loadBuildReady = (force) => load(force)

const fmt = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''
const ZONE = { green: '#1f7a4d', yellow: '#b7791f', red: '#b0413e' }
const SOURCE = { countersign: 'the countersignature', accepted: 'the recorded acceptance', 'first-hour': 'the first hour logged to Basic Services', manual: 'set by hand' }

// Where the plan starts, set by hand (tower seats only; the server checks too). Date, then the reason.
async function editStart(p) {
  const cur = p.target ? p.target.start : ''
  const date = await promptDialog({ title: 'Where does the plan start?', message: 'The day Basic Services work really began. By default it is the earlier of the countersignature and the first hour logged to a Basic Services phase.', defaultValue: cur || '', placeholder: 'YYYY-MM-DD', required: true })
  if (date === null) return
  const reason = await promptDialog({ title: 'Why this day?', message: 'Kept with the date.', multiline: true, required: true })
  if (reason === null) return
  const f = (ctx.currentMember && ctx.currentMember.fields) || {}
  try { await setBaselineStart({ projectId: p.projectId, projectName: p.name, date: date.trim(), reason, memberName: f['Member Name'] || f['Name'] || '' }) }
  catch (e) { await alertDialog({ title: 'Not saved', message: e.message || String(e) }) }
}

export default function BuildReadyStrip({ projectId, compact = false }) {
  const [data, setData] = useState(null)
  useEffect(() => {
    let alive = true
    load().then(d => { if (alive) setData(d) })
    const on = () => load(true).then(d => { if (alive) setData(d) })
    window.addEventListener('build-ready-changed', on)
    return () => { alive = false; window.removeEventListener('build-ready-changed', on) }
  }, [])
  const S = compact ? SC : SF
  const p = data && (data.projects || []).find(x => x.projectId === projectId)
  if (!p || !p.eligible) return null
  if (p.reached) return <div style={S.wrap}><span style={S.lbl}>Build Ready</span><span style={{ ...S.sub, color: ZONE.green }}>Reached. Construction is under way.</span></div>
  if (!p.target) {
    return (
      <div style={S.wrap}>
        <span style={S.lbl}>Build Ready</span>
        <span style={S.sub}>{compact ? 'No Build Ready date yet' : 'No Build Ready date yet. It is set from the day the Basic Services agreement is countersigned.'}</span>
      </div>
    )
  }
  const b = p.burn || { margin: 0, burned: 0, pct: 0, zone: 'green' }
  const left = p.target.left != null ? p.target.left : (data.today && p.target.date ? workdays(data.today, p.target.date) : null)
  const pct = Math.min(1, b.pct || 0)
  return (
    <div style={S.wrap} title={p.target.start
      ? `The plan from ${fmt(p.target.start)} (${SOURCE[p.target.startSource] || p.target.startSource}): ${p.target.chainDays} working days of work and waits, a ${p.target.bufferDays}-day buffer${p.target.holdDays ? ', ' + p.target.holdDays + ' days on hold' : ''}${b.late ? '' : ` · ${b.daysLeft != null ? b.daysLeft : p.target.bufferDays} buffer days left`}`
      : `Build Ready date set ${fmt(String(p.target.setAt || '').slice(0, 10))}${p.target.setBy ? ' by ' + p.target.setBy : ''}${p.target.reason ? ' · ' + p.target.reason : ''}`}>
      <span style={S.lbl}>Build Ready</span>
      <span style={{ ...S.t, ...(left < 0 ? { color: '#c0392b' } : null) }}>{left < 0 ? `T+${-left}` : `T-${left}`}</span>
      <span style={S.sub}>working days · {fmt(p.target.date)}</span>
      <span style={S.bar} aria-label={`${Math.round(pct * 100)} percent of the buffer burned`}>
        <i style={{ display: 'block', height: '100%', width: `${pct * 100}%`, background: ZONE[b.zone] || ZONE.green }} />
      </span>
      <span style={{ ...S.sub, color: ZONE[b.zone] || ZONE.green, fontWeight: 600 }}>
        {b.late ? `Late by ${-b.margin} working days` : `${b.done || 0}% of the plan done · ${Math.round(pct * 100)}% of the buffer used`}
      </span>
      {!compact && p.target.start && (hasModule('workload') || hasModule('admin')) && (
        <button type="button" onClick={() => editStart(p)} style={S.link}>Set start date…</button>
      )}
    </div>
  )
}

function workdays(a, b) { let n = 0, d = new Date(a + 'T12:00:00Z'); const end = new Date(b + 'T12:00:00Z'); while (d < end) { d = new Date(d.getTime() + 86400000); const w = d.getUTCDay(); if (w && w < 6) n++ } return n }

const SF = {
  wrap: { display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px 10px', margin: '2px 0 10px', fontFamily: '"DM Sans", sans-serif' },
  lbl: { fontFamily: 'Montserrat, sans-serif', fontSize: 10.5, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--aj-text-muted)' },
  t: { fontFamily: '"DM Mono", monospace', fontSize: 20, color: 'var(--aj-text)', fontVariantNumeric: 'tabular-nums' },
  sub: { fontSize: 12.5, color: 'var(--aj-text-muted)' },
  link: { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: 'var(--aj-text-muted)', textDecoration: 'underline', fontFamily: 'inherit' },
  bar: { display: 'inline-block', width: 120, height: 7, borderRadius: 4, background: 'var(--aj-bg-muted, #e3eaea)', overflow: 'hidden', alignSelf: 'center' },
}
// The Gantt toolbar's smaller version.
const SC = { ...SF, wrap: { ...SF.wrap, margin: 0, gap: '2px 8px' }, t: { ...SF.t, fontSize: 14 }, sub: { ...SF.sub, fontSize: 11.5 }, bar: { ...SF.bar, width: 70 } }
